import { supabase } from './supabase';

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service Worker not supported');
  }

  try {
    // Wait for the service worker registered by vite-plugin-pwa to be ready
    const registration = await navigator.serviceWorker.ready;
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    throw error;
  }
}

export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    throw new Error('Notifications not supported');
  }

  const permission = await Notification.requestPermission();
  return permission;
}

export async function checkNotificationPermission(): Promise<{
  permission: NotificationPermission;
  needsRenewal: boolean;
  hasValidSubscription: boolean;
}> {
  if (!('Notification' in window)) {
    return {
      permission: 'denied',
      needsRenewal: false,
      hasValidSubscription: false
    };
  }

  const permission = Notification.permission;
  
  // Check if we have a valid service worker registration
  let hasValidSubscription = false;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      // Test if the subscription is still valid by checking if it has valid keys
      hasValidSubscription = !!(subscription.endpoint && subscription.getKey && subscription.getKey('p256dh'));
    }
  } catch (error) {
    console.warn('Error checking push subscription:', error);
    hasValidSubscription = false;
  }

  const needsRenewal = permission !== 'granted' || !hasValidSubscription;

  return {
    permission,
    needsRenewal,
    hasValidSubscription
  };
}

export async function renewNotificationPermission(): Promise<boolean> {
  try {
    console.log('Renewing notification permission...');
    
    // Step 1: Request notification permission
    const permission = await requestNotificationPermission();
    
    if (permission !== 'granted') {
      console.log('Notification permission not granted:', permission);
      return false;
    }

    // Step 2: Register service worker
    const registration = await registerServiceWorker();
    
    // Step 3: Subscribe to push notifications
    const subscription = await subscribeToPushNotifications(registration);
    
    return !!subscription;
  } catch (error) {
    console.error('Failed to renew notification permission:', error);
    return false;
  }
}

export async function subscribeToPushNotifications(registration: ServiceWorkerRegistration) {
  try {
    // Unsubscribe from any existing subscription first
    let existingSubscription = await registration.pushManager.getSubscription();
    if (existingSubscription) {
      console.log('Unsubscribing from existing subscription...');
      await existingSubscription.unsubscribe();
    }

    // Create new subscription
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: import.meta.env.VITE_VAPID_PUBLIC_KEY
    });

    console.log('New push subscription created:', subscription.endpoint);

    // Save subscription to database
    await supabase.rpc('update_push_subscription', {
      p_subscription: subscription
    });

    return subscription;
  } catch (error) {
    console.error('Failed to subscribe to push notifications:', error);
    throw error;
  }
}

export async function sendPushNotification(subscription: PushSubscription, notification: {
  title: string;
  body: string;
  url?: string;
  actions?: { action: string; title: string }[];
}) {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/push-notification`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          subscription,
          notification
        })
      }
    );

    if (!response.ok) {
      throw new Error('Failed to send push notification');
    }

    return response.json();
  } catch (error) {
    console.error('Error sending push notification:', error);
    throw error;
  }
}

// Auto-check notification permissions every time the app becomes visible
export function setupNotificationPermissionMonitoring() {
  let isMonitoring = false;

  const checkAndRenew = async () => {
    try {
      const { needsRenewal, permission } = await checkNotificationPermission();
      
      if (needsRenewal && permission !== 'denied') {
        console.log('Notification permission needs renewal, attempting to renew...');
        
        // Only auto-renew if permission was previously granted but subscription is lost
        // Don't auto-prompt if user explicitly denied permissions
        if (permission === 'granted') {
          await renewNotificationPermission();
        } else {
          console.log('Notification permission not granted, skipping auto-renewal');
        }
      }
    } catch (error) {
      console.error('Error during permission monitoring:', error);
    }
  };

  const startMonitoring = () => {
    if (isMonitoring) return;
    
    isMonitoring = true;
    
    // Check immediately when monitoring starts
    checkAndRenew();
    
    // Check when page becomes visible (user switches back to tab)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        checkAndRenew();
      }
    });

    // Check when page gains focus
    window.addEventListener('focus', checkAndRenew);
    
    // Check periodically (every 5 minutes when active)
    const intervalId = setInterval(() => {
      if (!document.hidden) {
        checkAndRenew();
      }
    }, 5 * 60 * 1000); // 5 minutes

    // Cleanup function
    return () => {
      isMonitoring = false;
      document.removeEventListener('visibilitychange', checkAndRenew);
      window.removeEventListener('focus', checkAndRenew);
      clearInterval(intervalId);
    };
  };

  return startMonitoring();
}